import { useState } from "react";

import { Badge, Card, Empty, Panel } from "../../../components/ui";
import { LoadMore } from "../../../components/LoadMore";
import { ResourceView } from "../../../components/states";
import type { AppConfig } from "../../../config";
import { labeebPage, usePagedList } from "../../../hooks/usePagedList";
import { auditStamp } from "../../../api/mcp";
import {
  isTesterReport,
  labeebPaged,
  labeebTenantFeedbackPath,
  type LabeebFeedbackItem,
} from "../../../api/labeeb";
import { LabeebMissing } from "./labeebMissing";

/**
 * What this merchant's users said about the answers they got.
 *
 * REPORTS, NOT RATINGS. This used to be a thumbs up/down with an overlapping
 * `tester` tally, a category and a severity. The new API serves a report with
 * a `source`, a `status`, a free-text `description`, and the thread and turn
 * it came from — so a report is traceable back to the conversation instead of
 * summarised as a sentiment. There is no summary to total any more.
 *
 * Read-only, permanently: a report is the user's own words, and an API that
 * could edit it would make every remaining row untrustworthy as evidence.
 */
export function LabeebFeedbackPanel({ config, tenantId }: { config: AppConfig; tenantId: string }) {
  const feedback = usePagedList<LabeebFeedbackItem>(
    config,
    labeebPaged(labeebTenantFeedbackPath(tenantId)),
    labeebPage,
  );
  const state = feedback.state;
  const items = state.kind === "ready" ? state.data.rows : [];
  const [reread, setReread] = useState(false);

  function openScreenshot(event: { preventDefault: () => void }, expiresAt: string | undefined) {
    if (!expiresAt) return;
    const at = Date.parse(expiresAt);
    if (Number.isNaN(at) || at >= Date.now()) return;
    event.preventDefault();
    setReread(true);
    feedback.reload();
  }

  return (
    <Card title="Feedback">
      <ResourceView
        state={state}
        what="feedback"
        onRetry={feedback.reload}
        notFound={<LabeebMissing state={state} />}
      />

      {state.kind === "ready" && (
        <>
          {/* The "this page undercounts" notice is gone with the
              `complete` flag it read: the new payload is a plain cursor page
              with no partial-read signal, so claiming completeness either way
              would be an invention. */}
          {reread && (
            <p className="text-sm text-amber-700 dark:text-amber-400">
              This screenshot link expired. The page was re-read.
            </p>
          )}
          {items.length === 0 ? (
            <Empty>Nobody has filed a report yet.</Empty>
          ) : (
            <div className="mt-4 flex flex-col gap-3">
              {items.map((i, n) => (
                <Panel key={i.report_id || n} className="p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    {/* The source, not a rating — there is no sentiment in
                        this payload to colour a badge by. */}
                    <Badge tone={isTesterReport(i) ? "neutral" : "green"}>
                      {i.source ?? "unknown source"}
                    </Badge>
                    {i.status && <Badge tone="amber">{i.status}</Badge>}
                    <span className="ml-auto text-xs text-slate-400 dark:text-slate-500">
                      {i.created_at ? auditStamp(i.created_at, { dateOnly: true }) : "—"}
                    </span>
                  </div>
                  {/* Free text a user typed. React escapes it; it is never
                      rendered as markup. */}
                  {i.description && <p className="mt-1.5 text-sm">{i.description}</p>}

                  {/* WHERE TO LOOK. The thread and turn make a report
                      reproducible — the replacement for the browser-context
                      block the old payload carried. */}
                  {(i.thread_id || i.turn_id || i.model_version) && (
                    <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 border-t border-slate-200 pt-2 text-xs dark:border-slate-700">
                      {i.thread_id && (
                        <div className="flex gap-1">
                          <dt className="text-slate-500 dark:text-slate-400">thread</dt>
                          <dd className="font-mono text-slate-700 dark:text-slate-300">
                            {i.thread_id}
                          </dd>
                        </div>
                      )}
                      {i.turn_id && (
                        <div className="flex gap-1">
                          <dt className="text-slate-500 dark:text-slate-400">turn</dt>
                          <dd className="font-mono text-slate-700 dark:text-slate-300">
                            {i.turn_id}
                          </dd>
                        </div>
                      )}
                      {i.model_version && (
                        <div className="flex gap-1">
                          <dt className="text-slate-500 dark:text-slate-400">model</dt>
                          <dd className="font-mono text-slate-700 dark:text-slate-300">
                            {i.model_version}
                          </dd>
                        </div>
                      )}
                    </dl>
                  )}

                  {/* THE URL EXPIRES. It is a presigned link, so the panel
                      says when it dies rather than handing an operator an
                      address that will quietly stop working. New tab and
                      noreferrer, because a stored URL is still data we did
                      not author. */}
                  {i.screenshot?.url && (
                    <p className="mt-2 text-xs">
                      <a
                        className="text-sky-600 underline dark:text-sky-400"
                        href={i.screenshot.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        onClick={(event) => openScreenshot(event, i.screenshot?.expires_at)}
                      >
                        Screenshot
                      </a>
                      {i.screenshot.expires_at && (
                        <span className="ml-2 text-slate-500 dark:text-slate-400">
                          link expires {auditStamp(i.screenshot.expires_at)}
                        </span>
                      )}
                    </p>
                  )}
                </Panel>
              ))}
            </div>
          )}

          {/* Feedback written without a tenant is filed under the literal
              tenant `default` and never reaches a real tenant's route. An
              empty list is not proof a merchant left none. */}
          {items.length === 0 && (
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
              Feedback submitted without a tenant is filed separately and does
              not appear here.
            </p>
          )}
          <LoadMore
            shown={items.length}
            noun="reports"
            hasMore={state.data.next !== null}
            loading={feedback.loadingMore}
            error={feedback.moreError}
            onMore={feedback.loadMore}
          />
        </>
      )}
    </Card>
  );
}
